import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertCircle,
  Box,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  Download,
  ExternalLink,
  FileDown,
  FileText,
  Globe,
  MapPin,
  Package,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  Settings,
  Trash2,
  Truck,
  X,
  Wallet,
  Zap,
} from 'lucide-react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Badge, EmptyState, Input, Spinner } from '@terra/ui';

import { api } from '../lib/api';

import { CourierMoneyTab } from './courierMoney';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type ShipmentStatus =
  | 'PENDING'
  | 'AWB_ASSIGNED'
  | 'PICKUP_SCHEDULED'
  | 'PICKED_UP'
  | 'IN_TRANSIT'
  | 'OUT_FOR_DELIVERY'
  | 'DELIVERED'
  | 'RTO_INITIATED'
  | 'RTO_DELIVERED'
  | 'CANCELLED'
  | 'FAILED';

const SHIPMENT_STATUSES: ShipmentStatus[] = [
  'PENDING',
  'AWB_ASSIGNED',
  'PICKUP_SCHEDULED',
  'PICKED_UP',
  'IN_TRANSIT',
  'OUT_FOR_DELIVERY',
  'DELIVERED',
  'RTO_INITIATED',
  'RTO_DELIVERED',
  'CANCELLED',
  'FAILED',
];

const STATUS_META: Record<
  ShipmentStatus,
  { label: string; tone: 'neutral' | 'info' | 'success' | 'warning' | 'danger' }
> = {
  PENDING: { label: 'Pending', tone: 'neutral' },
  // Courier booked and AWB issued, but the pickup call has not succeeded yet.
  // Warning tone on purpose: this one needs a human to retry the pickup.
  AWB_ASSIGNED: { label: 'Awaiting Pickup', tone: 'warning' },
  PICKUP_SCHEDULED: { label: 'Pickup Scheduled', tone: 'info' },
  PICKED_UP: { label: 'Picked Up', tone: 'info' },
  IN_TRANSIT: { label: 'In Transit', tone: 'info' },
  OUT_FOR_DELIVERY: { label: 'Out for Delivery', tone: 'warning' },
  DELIVERED: { label: 'Delivered', tone: 'success' },
  RTO_INITIATED: { label: 'RTO Initiated', tone: 'danger' },
  RTO_DELIVERED: { label: 'RTO Delivered', tone: 'danger' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
  FAILED: { label: 'Failed', tone: 'danger' },
};

interface TrackingStep {
  status: ShipmentStatus;
  label: string;
  icon: React.ElementType;
}

const TRACKING_TIMELINE: TrackingStep[] = [
  { status: 'PENDING', label: 'Order Created', icon: Box },
  { status: 'AWB_ASSIGNED', label: 'AWB Assigned', icon: Package },
  { status: 'PICKUP_SCHEDULED', label: 'Pickup Scheduled', icon: Clock },
  { status: 'PICKED_UP', label: 'Picked Up', icon: Package },
  { status: 'IN_TRANSIT', label: 'In Transit', icon: Truck },
  { status: 'OUT_FOR_DELIVERY', label: 'Out for Delivery', icon: MapPin },
  { status: 'DELIVERED', label: 'Delivered', icon: CheckCircle },
];

interface ShippingMethod {
  id: string;
  zone: string;
  methodName: string;
  costPaise: number;
  freeLimitPaise: number;
}

interface CourierRate {
  courier_company_id: number;
  courier_name: string;
  rate: number;
  etd?: string;
  freight_charge?: number;
  cod_charges?: number;
}

interface ShippingDocuments {
  labelUrl?: string | null;
  manifestUrl?: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// useSettingMutation (local copy — avoids cross-module import)
// ─────────────────────────────────────────────────────────────────────────────
function useSettingMutation(key: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (value: unknown) => api.cms.upsertSetting(key, value),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['website-setting', key] });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared tab button
// ─────────────────────────────────────────────────────────────────────────────
function Tab({
  active,
  onClick,
  icon: Icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ElementType;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 rounded-xl px-4 py-2 text-[13px] font-bold transition-all ${
        active
          ? 'bg-ink text-white shadow-sm'
          : 'text-ink-muted hover:text-ink hover:bg-surface-subtle'
      }`}
    >
      <Icon className="h-4 w-4 shrink-0" />
      {label}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. Shipments Table Tab
// ─────────────────────────────────────────────────────────────────────────────
function ShipmentsTab() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  // `?q=` lets other screens (an order's detail page) open straight onto a shipment.
  const initialQuery = searchParams.get('q') ?? '';
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(initialQuery);
  const [statusFilter, setStatusFilter] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(initialQuery);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [awbModal, setAwbModal] = useState<any>(null);
  const [trackModal, setTrackModal] = useState<any>(null);
  const [bookModal, setBookModal] = useState<any>(null);
  const [documentsModal, setDocumentsModal] = useState<any>(null);
  const [selectedCourierId, setSelectedCourierId] = useState<number | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-shipments', page, debouncedSearch, statusFilter],
    queryFn: () => {
      const params: { page: number; pageSize: number; q?: string; status?: string } = {
        page,
        pageSize: 20,
      };
      if (debouncedSearch) params.q = debouncedSearch;
      if (statusFilter) params.status = statusFilter;
      return api.orders.listShipments(params);
    },
  });

  const shipments: any[] = data?.data ?? [];
  const pagination = data?.page;

  // AWB assignment mutation
  const awbMutation = useMutation({
    mutationFn: ({ id, ...body }: any) => api.orders.updateProviderDetails(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-shipments'] });
      setAwbModal(null);
    },
  });

  // Tracking event mutation
  const trackMutation = useMutation({
    mutationFn: ({ id, ...body }: any) => api.orders.createTrackingEvent(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-shipments'] });
      setTrackModal(null);
    },
  });

  // Serviceable Rates Query
  const {
    data: rates = [],
    isError: isRatesError,
    error: ratesError,
    isLoading: isLoadingRates,
  } = useQuery<CourierRate[]>({
    queryKey: ['shipment-rates', bookModal?.id],
    queryFn: () => api.orders.getServiceableRates(bookModal.id),
    enabled: !!bookModal,
  });

  // Book Courier Mutation
  const bookMutation = useMutation({
    mutationFn: ({ id, courierId }: { id: string; courierId: number }) =>
      api.orders.bookCourier(id, courierId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-shipments'] });
      setBookModal(null);
      setSelectedCourierId(null);
    },
  });

  // Download Documents Mutation
  const docsMutation = useMutation({
    mutationFn: (shipmentId: string) =>
      api.orders.downloadDocuments(shipmentId) as Promise<ShippingDocuments>,
  });

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="border-border/60 bg-surface flex flex-col gap-3 rounded-2xl border p-4 shadow-sm sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="text-ink-muted absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by AWB, courier, order number or vendor..."
            className="border-border/60 bg-surface-subtle text-ink placeholder:text-ink-muted h-10 w-full rounded-xl border pl-9 pr-4 text-[13px] font-medium outline-none focus:ring-2 focus:ring-blue-500/30"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="border-border/60 bg-surface-subtle text-ink h-10 rounded-xl border px-3 text-[13px] font-medium outline-none focus:ring-2 focus:ring-blue-500/30"
        >
          <option value="">All Statuses</option>
          {SHIPMENT_STATUSES.map((s) => (
            <option key={s} value={s}>
              {STATUS_META[s].label}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 bg-surface-subtle/30 flex items-center justify-between border-b px-5 py-4">
          <div className="flex items-center gap-2">
            <Truck className="text-ink-muted h-4 w-4" />
            <span className="text-ink text-[14px] font-bold">
              All Shipments
              {pagination?.total != null && (
                <span className="text-ink-muted ml-2 font-mono text-[12px]">
                  ({pagination.total} total)
                </span>
              )}
            </span>
          </div>
          <button
            onClick={() => void queryClient.invalidateQueries({ queryKey: ['admin-shipments'] })}
            className="text-ink-muted hover:text-ink hover:bg-surface-subtle grid h-8 w-8 place-items-center rounded-lg transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>

        {isLoading ? (
          <div className="grid min-h-[300px] place-items-center">
            <Spinner size="lg" />
          </div>
        ) : shipments.length === 0 ? (
          <div className="p-8">
            <EmptyState
              icon={<Truck className="text-ink-muted h-8 w-8" />}
              title="No shipments found"
              description="Shipments will appear here once vendors start fulfilling orders."
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                  <th className="w-8 py-3 pl-4 pr-2" />
                  <th className="py-3 pl-2 pr-4">Order / Vendor</th>
                  <th className="py-3 pr-4">AWB / Courier</th>
                  <th className="py-3 pr-4 text-center">Weight</th>
                  <th className="py-3 pr-4 text-center">Status</th>
                  <th className="py-3 pr-4">Created</th>
                  <th className="py-3 pr-5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-border/40 divide-y text-[13px]">
                {shipments.map((s: any) => (
                  <Fragment key={s.id}>
                    <tr
                      className="hover:bg-surface-subtle/40 cursor-pointer transition-colors"
                      onClick={() => setExpandedId(expandedId === s.id ? null : s.id)}
                    >
                      <td className="py-3 pl-4 pr-2">
                        {expandedId === s.id ? (
                          <ChevronDown className="text-ink-muted h-4 w-4" />
                        ) : (
                          <ChevronRight className="text-ink-muted h-4 w-4" />
                        )}
                      </td>
                      <td className="py-3 pl-2 pr-4">
                        <p className="text-ink font-bold">
                          #{s.subOrder?.orderGroup?.orderNumber ?? '—'}
                        </p>
                        <p className="text-ink-muted text-[12px] font-medium">
                          {s.subOrder?.vendor?.storeName ?? '—'}
                        </p>
                      </td>
                      <td className="py-3 pr-4">
                        {s.awbNumber ? (
                          <p className="text-ink font-mono text-[12px] font-bold">{s.awbNumber}</p>
                        ) : (
                          <span className="text-ink-muted text-[12px] italic">No AWB</span>
                        )}
                        {s.courierName && (
                          <p className="text-ink-muted text-[12px] font-medium">{s.courierName}</p>
                        )}
                      </td>
                      <td className="py-3 pr-4 text-center">
                        <span className="text-ink-muted font-mono text-[12px]">
                          {s.weightGrams ? `${s.weightGrams}g` : '—'}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-center">
                        <Badge tone={STATUS_META[s.status as ShipmentStatus]?.tone ?? 'neutral'}>
                          {STATUS_META[s.status as ShipmentStatus]?.label ?? s.status}
                        </Badge>
                      </td>
                      <td className="py-3 pr-4">
                        <span className="text-ink-muted text-[12px]">
                          {new Date(s.createdAt).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="py-3 pr-5" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-2">
                          {s.status === 'PENDING' ? (
                            <>
                              <button
                                onClick={() => {
                                  setSelectedCourierId(null);
                                  setBookModal(s);
                                }}
                                className="bg-ink hover:bg-ink/90 rounded-lg px-2.5 py-1 text-[11px] font-bold text-white shadow-sm transition-colors"
                              >
                                Book Shiprocket
                              </button>
                              <button
                                onClick={() => setAwbModal(s)}
                                className="border-border/60 bg-surface-subtle text-ink-muted hover:text-ink rounded-lg border px-2 py-1 text-[11px] font-bold transition-colors"
                              >
                                Assign AWB
                              </button>
                            </>
                          ) : (
                            <>
                              {s.awbNumber && (
                                <button
                                  onClick={() => {
                                    setDocumentsModal(s);
                                    docsMutation.mutate(s.id);
                                  }}
                                  className="flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px] font-bold text-emerald-700 transition-colors hover:bg-emerald-100"
                                >
                                  <FileText className="h-3 w-3" />
                                  Documents
                                </button>
                              )}
                              <button
                                onClick={() => setTrackModal(s)}
                                className="border-border/60 bg-surface-subtle text-ink-muted hover:text-ink rounded-lg border px-2 py-1 text-[11px] font-bold transition-colors"
                              >
                                Update Track
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Expanded tracking timeline */}
                    <AnimatePresence>
                      {expandedId === s.id && (
                        <tr key={`${s.id}-expanded`}>
                          <td colSpan={7} className="p-0">
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              <div className="bg-surface-subtle/50 border-border/40 border-t px-6 py-4">
                                <p className="text-ink-muted mb-3 text-[11px] font-bold uppercase tracking-wider">
                                  Tracking History
                                </p>
                                {s.trackingEvents && s.trackingEvents.length > 0 ? (
                                  <div className="space-y-2">
                                    {s.trackingEvents.map((ev: any, i: number) => (
                                      <div key={ev.id ?? i} className="flex items-start gap-3">
                                        <div className="bg-ink/10 mt-1 h-2 w-2 shrink-0 rounded-full" />
                                        <div>
                                          <p className="text-ink text-[12px] font-bold">
                                            {STATUS_META[ev.status as ShipmentStatus]?.label ??
                                              ev.status}
                                            {ev.location && (
                                              <span className="text-ink-muted font-medium">
                                                {' '}
                                                · {ev.location}
                                              </span>
                                            )}
                                          </p>
                                          {ev.description && (
                                            <p className="text-ink-muted text-[12px]">
                                              {ev.description}
                                            </p>
                                          )}
                                          <p className="text-ink-muted text-[11px]">
                                            {new Date(ev.occurredAt).toLocaleString()}
                                          </p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-ink-muted text-[12px] italic">
                                    No tracking events yet.
                                  </p>
                                )}
                              </div>
                            </motion.div>
                          </td>
                        </tr>
                      )}
                    </AnimatePresence>
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.pageCount > 1 && (
          <div className="border-border/60 bg-surface-subtle/30 flex items-center justify-between border-t px-5 py-3">
            <span className="text-ink-muted text-[12px] font-medium">
              Page {pagination.page} of {pagination.pageCount}
            </span>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="border-border/60 bg-surface text-ink disabled:text-ink-muted flex h-8 items-center rounded-lg border px-3 text-[12px] font-bold transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Previous
              </button>
              <button
                disabled={page >= pagination.pageCount}
                onClick={() => setPage((p) => p + 1)}
                className="border-border/60 bg-surface text-ink disabled:text-ink-muted flex h-8 items-center rounded-lg border px-3 text-[12px] font-bold transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Assign AWB Modal */}
      <AnimatePresence>
        {awbModal && (
          <Modal title="Assign AWB & Courier" onClose={() => setAwbModal(null)}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const fd = new FormData(e.currentTarget);
                awbMutation.mutate({
                  id: awbModal.id,
                  awbNumber: (fd.get('awbNumber') as string) || undefined,
                  courierName: (fd.get('courierName') as string) || undefined,
                  providerShipmentId: (fd.get('providerShipmentId') as string) || undefined,
                  providerOrderId: (fd.get('providerOrderId') as string) || undefined,
                });
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-3">
                <Input
                  name="awbNumber"
                  label="AWB Number"
                  placeholder={awbModal.awbNumber ?? 'Enter AWB...'}
                  defaultValue={awbModal.awbNumber ?? ''}
                />
                <Input
                  name="courierName"
                  label="Courier Name"
                  placeholder={awbModal.courierName ?? 'e.g. Shiprocket'}
                  defaultValue={awbModal.courierName ?? ''}
                />
                <Input
                  name="providerShipmentId"
                  label="Provider Shipment ID"
                  placeholder="Provider Shipment ID"
                  defaultValue={awbModal.providerShipmentId ?? ''}
                />
                <Input
                  name="providerOrderId"
                  label="Provider Order ID"
                  placeholder="Provider Order ID"
                  defaultValue={awbModal.providerOrderId ?? ''}
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setAwbModal(null)}
                  className="border-border/60 text-ink-muted hover:text-ink h-9 rounded-xl border px-4 text-[13px] font-bold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={awbMutation.isPending}
                  className="bg-ink hover:bg-ink/90 flex h-9 items-center gap-2 rounded-xl px-5 text-[13px] font-bold text-white disabled:opacity-50"
                >
                  {awbMutation.isPending ? <Spinner size="sm" className="text-white" /> : null}
                  Save AWB
                </button>
              </div>
            </form>
          </Modal>
        )}
      </AnimatePresence>

      {/* Update Tracking Modal */}
      <AnimatePresence>
        {trackModal && (
          <Modal title="Add Tracking Event" onClose={() => setTrackModal(null)}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const fd = new FormData(e.currentTarget);
                const body: any = { status: fd.get('status') as string };
                const desc = fd.get('description') as string;
                const loc = fd.get('location') as string;
                if (desc) body.description = desc;
                if (loc) body.location = loc;
                trackMutation.mutate({ id: trackModal.id, ...body });
              }}
              className="space-y-4"
            >
              <div>
                <label className="text-ink mb-1 block text-[13px] font-bold">New Status</label>
                <select
                  name="status"
                  required
                  className="border-border/60 bg-surface-subtle text-ink h-10 w-full rounded-xl border px-3 text-[13px] font-medium outline-none focus:ring-2 focus:ring-blue-500/30"
                >
                  {SHIPMENT_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {STATUS_META[s].label}
                    </option>
                  ))}
                </select>
              </div>
              <Input name="location" label="Location (optional)" placeholder="e.g. Mumbai Hub" />
              <Input
                name="description"
                label="Description (optional)"
                placeholder="e.g. Package received at hub"
              />
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setTrackModal(null)}
                  className="border-border/60 text-ink-muted hover:text-ink h-9 rounded-xl border px-4 text-[13px] font-bold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={trackMutation.isPending}
                  className="bg-ink hover:bg-ink/90 flex h-9 items-center gap-2 rounded-xl px-5 text-[13px] font-bold text-white disabled:opacity-50"
                >
                  {trackMutation.isPending ? <Spinner size="sm" className="text-white" /> : null}
                  Add Event
                </button>
              </div>
            </form>
          </Modal>
        )}
      </AnimatePresence>

      {/* Book Shiprocket Courier Modal */}
      <AnimatePresence>
        {bookModal && (
          <Modal
            title="Book Shiprocket Courier"
            onClose={() => {
              setBookModal(null);
              setSelectedCourierId(null);
            }}
          >
            <div className="space-y-4">
              <div className="border-border/60 bg-surface-subtle/50 rounded-xl border p-3">
                <p className="text-ink text-[12px] font-bold">
                  Shipment ID:{' '}
                  <span className="text-ink-muted font-mono text-[11px]">{bookModal.id}</span>
                </p>
                <p className="text-ink text-[12px] font-bold">
                  Sub-order:{' '}
                  <span className="text-ink-muted font-mono text-[11px]">
                    #{bookModal.subOrder?.orderGroup?.orderNumber ?? '—'}
                  </span>
                </p>
                <p className="text-ink text-[12px] font-bold">
                  Weight:{' '}
                  <span className="text-ink-muted font-mono text-[11px]">
                    {bookModal.weightGrams ?? 0}g
                  </span>
                </p>
              </div>

              {isLoadingRates ? (
                <div className="flex flex-col items-center justify-center gap-2 py-8">
                  <Spinner size="md" />
                  <p className="text-ink-muted text-[12px] font-medium">
                    Fetching serviceable rates from Shiprocket...
                  </p>
                </div>
              ) : isRatesError ? (
                <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-[13px] font-medium text-red-700">
                  Could not fetch serviceable courier rates.{' '}
                  {(ratesError as any)?.message ||
                    'Please verify pickup and delivery details, then try again.'}
                </div>
              ) : !rates || rates.length === 0 ? (
                <div className="rounded-xl border border-orange-200 bg-orange-50 p-4 text-[13px] font-medium text-orange-700">
                  No serviceable courier rates returned. Please check the warehouse pincode and
                  customer shipping pincode.
                </div>
              ) : (
                <div className="space-y-3">
                  <label className="text-ink block text-[13px] font-bold">
                    Select Serviceable Courier Partner
                  </label>
                  <div className="max-h-[240px] space-y-2 overflow-y-auto pr-1">
                    {rates.map((rate) => {
                      const isSelected = selectedCourierId === rate.courier_company_id;
                      return (
                        <div
                          key={rate.courier_company_id}
                          onClick={() => setSelectedCourierId(rate.courier_company_id)}
                          className={`flex cursor-pointer items-center justify-between rounded-xl border p-3 transition-all ${
                            isSelected
                              ? 'border-ink bg-ink/5 shadow-sm'
                              : 'border-border/60 hover:bg-surface-subtle/50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="bg-surface-subtle border-border/60 grid h-8 w-8 place-items-center rounded-lg border text-[14px]">
                              🚚
                            </div>
                            <div>
                              <p className="text-ink text-[13px] font-bold">{rate.courier_name}</p>
                              <p className="text-ink-muted text-[11px] font-medium">
                                ETD: {rate.etd || '—'}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-ink font-mono text-[14px] font-extrabold">
                              ₹{Number(rate.rate).toFixed(2)}
                            </p>
                            {(rate.cod_charges ?? 0) > 0 && (
                              <p className="text-ink-muted text-[10px] font-medium">
                                COD Fee: ₹{rate.cod_charges}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="border-border/40 flex justify-end gap-2 border-t pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setBookModal(null);
                    setSelectedCourierId(null);
                  }}
                  className="border-border/60 text-ink-muted hover:text-ink h-9 rounded-xl border px-4 text-[13px] font-bold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={isRatesError || selectedCourierId === null || bookMutation.isPending}
                  onClick={() => {
                    if (selectedCourierId !== null) {
                      bookMutation.mutate({ id: bookModal.id, courierId: selectedCourierId });
                    }
                  }}
                  className="bg-ink hover:bg-ink/90 flex h-9 items-center gap-2 rounded-xl px-5 text-[13px] font-bold text-white shadow-sm disabled:opacity-40"
                >
                  {bookMutation.isPending ? <Spinner size="sm" className="text-white" /> : null}
                  Book Shipment
                </button>
              </div>
            </div>
          </Modal>
        )}
      </AnimatePresence>

      {/* Shiprocket Documents Modal */}
      <AnimatePresence>
        {documentsModal && (
          <Modal
            title="Shipping Documents"
            onClose={() => {
              setDocumentsModal(null);
              docsMutation.reset();
            }}
          >
            <div className="space-y-4">
              <div className="border-border/60 bg-surface-subtle/50 rounded-xl border p-3">
                <p className="text-ink text-[12px] font-bold">
                  Courier:{' '}
                  <span className="text-ink-muted font-semibold">
                    {documentsModal.courierName ?? '—'}
                  </span>
                </p>
                <p className="text-ink text-[12px] font-bold">
                  AWB Code:{' '}
                  <span className="text-ink-muted font-mono text-[11px] font-bold">
                    {documentsModal.awbNumber ?? '—'}
                  </span>
                </p>
              </div>

              {docsMutation.isPending ? (
                <div className="flex flex-col items-center justify-center gap-2 py-8">
                  <Spinner size="md" />
                  <p className="text-ink-muted text-[12px] font-medium">
                    Generating documents via Shiprocket...
                  </p>
                </div>
              ) : docsMutation.isError ? (
                <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-[13px] font-medium text-red-700">
                  Failed to fetch documents. Error:{' '}
                  {(docsMutation.error as any)?.message || 'Unknown error'}
                </div>
              ) : docsMutation.data?.labelUrl || docsMutation.data?.manifestUrl ? (
                <div className="grid grid-cols-2 gap-3">
                  {docsMutation.data.labelUrl && (
                    <a
                      href={docsMutation.data.labelUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="border-border/60 hover:border-ink hover:bg-surface-subtle/30 animate-fadeIn group flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border p-4 text-center shadow-sm transition-all"
                    >
                      <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-50 text-[16px] text-emerald-700">
                        <FileDown className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-ink text-[13px] font-bold group-hover:underline">
                          Print Label
                        </p>
                        <p className="text-ink-muted mt-0.5 text-[11px] font-medium">
                          Shipping box barcode PDF
                        </p>
                      </div>
                    </a>
                  )}

                  {docsMutation.data.manifestUrl && (
                    <a
                      href={docsMutation.data.manifestUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="border-border/60 hover:border-ink hover:bg-surface-subtle/30 animate-fadeIn group flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border p-4 text-center shadow-sm transition-all"
                    >
                      <div className="grid h-10 w-10 place-items-center rounded-xl bg-blue-50 text-[16px] text-blue-700">
                        <FileDown className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-ink text-[13px] font-bold group-hover:underline">
                          Print Manifest
                        </p>
                        <p className="text-ink-muted mt-0.5 text-[11px] font-medium">
                          Courier handover sign sheet
                        </p>
                      </div>
                    </a>
                  )}
                </div>
              ) : docsMutation.data ? (
                <div className="rounded-xl border border-orange-200 bg-orange-50 p-4 text-[13px] font-medium text-orange-700">
                  Documents were requested, but no label or manifest URL was returned.
                </div>
              ) : null}

              <div className="border-border/40 flex justify-end gap-2 border-t pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setDocumentsModal(null);
                    docsMutation.reset();
                  }}
                  className="border-border/60 text-ink-muted hover:text-ink h-9 rounded-xl border px-5 text-[13px] font-bold transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </Modal>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. Courier Integrations Tab
// ─────────────────────────────────────────────────────────────────────────────
const COURIERS = [
  {
    id: 'shiprocket',
    name: 'Shiprocket',
    logo: '🚀',
    description:
      "India's #1 courier aggregator platform — integrates 17+ partners including Blue Dart, Delhivery, DTDC.",
    fields: [
      { name: 'email', label: 'Account Email', type: 'email', placeholder: 'user@shiprocket.in' },
      { name: 'password', label: 'Account Password', type: 'password', placeholder: '••••••••' },
      { name: 'channelId', label: 'Channel ID', type: 'text', placeholder: 'Channel ID' },
      {
        name: 'webhookToken',
        label: 'Webhook security token',
        type: 'password',
        placeholder: 'the x-api-key set on the Shiprocket webhook',
      },
    ],
  },
  {
    id: 'delhivery',
    name: 'Delhivery',
    logo: '📦',
    description:
      'Delhivery B2B & B2C logistics API — pan-India last-mile and surface express delivery.',
    fields: [
      { name: 'apiToken', label: 'API Token', type: 'password', placeholder: 'Bearer token...' },
      { name: 'clientName', label: 'Client Name', type: 'text', placeholder: 'Your client name' },
    ],
  },
  {
    id: 'mock',
    name: 'Mock Carrier (Sandbox)',
    logo: '🧪',
    description:
      'Simulate courier flows in development. All webhooks are handled internally without external API calls.',
    fields: [{ name: 'enabled', label: 'Enable Mock Carrier', type: 'checkbox', placeholder: '' }],
  },
];

/** Signs in to Shiprocket with the saved credentials — run it after saving. */
function ShiprocketTestButton() {
  const test = useMutation({ mutationFn: () => api.cms.testIntegration('shiprocket') });
  return (
    <div className="flex flex-wrap items-center gap-3">
      <button
        type="button"
        onClick={() => test.mutate()}
        disabled={test.isPending}
        className="border-border hover:bg-surface-subtle flex h-9 items-center gap-2 rounded-xl border px-4 text-[12.5px] font-bold disabled:opacity-50"
      >
        {test.isPending ? <Spinner size="sm" /> : null}
        Test connection
      </button>
      {test.data && (
        <span
          role="status"
          className={`text-[12.5px] font-semibold ${test.data.ok ? 'text-emerald-700' : 'text-red-700'}`}
        >
          {test.data.ok ? '✓ ' : '✕ '}
          {test.data.message}
        </span>
      )}
    </div>
  );
}

function CourierIntegrationsTab() {
  const { data: setting } = useQuery({
    queryKey: ['website-setting', 'courier_settings'],
    queryFn: () => api.cms.getSetting('courier_settings').catch(() => null),
  });
  const mutation = useSettingMutation('courier_settings');
  const qcLive = useQueryClient();
  const savedValues = useMemo(() => (setting?.value as any) ?? {}, [setting]);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [inited, setInited] = useState(false);
  const [expanded, setExpanded] = useState<string | null>('shiprocket');

  useEffect(() => {
    if (setting && !inited) {
      setFormData(savedValues);
      setInited(true);
    }
  }, [setting, inited, savedValues]);

  const handleSave = (courierId: string) => {
    const updated = { ...savedValues, ...formData };
    mutation.mutate(updated, {
      onSuccess: () => void qcLive.invalidateQueries({ queryKey: ['effective-config'] }),
    });
  };

  // What the server is actually using — admin values first, the server's .env
  // for anything left blank. The form below only shows what is saved here, so
  // without this a platform running on .env credentials read "Not configured".
  const { data: live } = useQuery({
    queryKey: ['effective-config'],
    queryFn: () => api.cms.getEffectiveConfig(),
  });

  const isConnected = (courierId: string) => {
    const vals = savedValues[courierId] ?? {};
    if (courierId === 'shiprocket')
      return live?.shipping.configured ?? !!(vals.email && vals.password);
    if (courierId === 'delhivery') return !!vals.apiToken;
    if (courierId === 'mock') return !!vals.enabled;
    return false;
  };

  return (
    <div className="space-y-4">
      {COURIERS.map((courier) => {
        const connected = isConnected(courier.id);
        const isOpen = expanded === courier.id;

        return (
          <div
            key={courier.id}
            className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm"
          >
            <button
              onClick={() => setExpanded(isOpen ? null : courier.id)}
              className="hover:bg-surface-subtle/30 flex w-full items-center justify-between p-5 text-left transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="bg-surface-subtle border-border/60 grid h-12 w-12 shrink-0 place-items-center rounded-xl border text-2xl shadow-sm">
                  {courier.logo}
                </div>
                <div>
                  <h3 className="text-ink text-[14px] font-bold">{courier.name}</h3>
                  <p className="text-ink-muted mt-0.5 max-w-xl text-[12px] font-medium">
                    {courier.description}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 pl-4">
                <span
                  className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold ${
                    connected
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-surface-subtle text-ink-muted'
                  }`}
                >
                  {connected ? (
                    <CheckCircle className="h-3 w-3" />
                  ) : (
                    <AlertCircle className="h-3 w-3" />
                  )}
                  {connected ? 'Connected' : 'Not configured'}
                </span>
                {isOpen ? (
                  <ChevronDown className="text-ink-muted h-4 w-4" />
                ) : (
                  <ChevronRight className="text-ink-muted h-4 w-4" />
                )}
              </div>
            </button>

            <AnimatePresence>
              {isOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="border-border/60 border-t p-5">
                    {courier.id === 'shiprocket' && live && (
                      <div
                        className={`mb-4 rounded-xl border px-4 py-3 text-[12.5px] font-medium leading-relaxed ${
                          live.shipping.configured && live.shipping.webhookTokenSet
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                            : 'border-amber-200 bg-amber-50 text-amber-900'
                        }`}
                      >
                        <p className="font-bold">
                          Live now:{' '}
                          {live.shipping.configured
                            ? `signed in as ${live.shipping.account ?? 'the configured account'}`
                            : 'no Shiprocket account configured'}
                          {' · '}
                          {live.shipping.source === 'db'
                            ? 'from these admin settings'
                            : live.shipping.source === 'env'
                              ? 'from the server .env'
                              : live.shipping.source === 'mixed'
                                ? 'partly admin settings, partly the server .env'
                                : 'nothing set'}
                          {' · '}
                          webhook token {live.shipping.webhookTokenSet ? 'set ✓' : 'missing ✗'}
                        </p>
                        <p className="mt-0.5">
                          A field left blank here uses the server&apos;s .env value; a value saved
                          here overrides it straight away, no redeploy needed. Secrets saved here
                          show as ••••••••.
                        </p>
                        {live.shipping.quotedValue && (
                          <p className="mt-1 font-bold text-rose-700">
                            A .env value is wrapped in quotes — the server now strips them, but
                            remove them from the .env to be safe.
                          </p>
                        )}
                      </div>
                    )}
                    <div className="grid gap-4 sm:grid-cols-2">
                      {courier.fields.map((field) => {
                        const key = `${courier.id}.${field.name}`;
                        const value = formData[courier.id]?.[field.name] ?? '';

                        if (field.type === 'checkbox') {
                          return (
                            <div key={field.name} className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                id={key}
                                checked={!!formData[courier.id]?.enabled}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    [courier.id]: {
                                      ...(prev[courier.id] ?? {}),
                                      enabled: e.target.checked,
                                    },
                                  }))
                                }
                                className="accent-ink h-4 w-4 rounded"
                              />
                              <label htmlFor={key} className="text-ink text-[13px] font-bold">
                                {field.label}
                              </label>
                            </div>
                          );
                        }

                        return (
                          <Input
                            key={field.name}
                            label={field.label}
                            type={field.type}
                            placeholder={field.placeholder}
                            value={value}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                [courier.id]: {
                                  ...(prev[courier.id] ?? {}),
                                  [field.name]: e.target.value,
                                },
                              }))
                            }
                          />
                        );
                      })}
                    </div>

                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                      {courier.id === 'shiprocket' ? <ShiprocketTestButton /> : <span />}
                      <button
                        onClick={() => handleSave(courier.id)}
                        disabled={mutation.isPending}
                        className="bg-ink hover:bg-ink/90 flex h-9 items-center gap-2 rounded-xl px-5 text-[13px] font-bold text-white disabled:opacity-50"
                      >
                        {mutation.isPending ? <Spinner size="sm" className="text-white" /> : null}
                        Save {courier.name} Settings
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. Shipping Zones Tab
// ─────────────────────────────────────────────────────────────────────────────
function ShippingZonesTab() {
  const { data: setting, isLoading } = useQuery({
    queryKey: ['website-setting', 'shipping_settings'],
    queryFn: () => api.cms.getSetting('shipping_settings').catch(() => null),
  });
  const mutation = useSettingMutation('shipping_settings');

  const methods: ShippingMethod[] = useMemo(
    () =>
      (setting?.value as ShippingMethod[]) ?? [
        {
          id: '1',
          zone: 'Domestic (India)',
          methodName: 'Flat Rate Standard',
          costPaise: 4900,
          freeLimitPaise: 99900,
        },
        {
          id: '2',
          zone: 'International',
          methodName: 'Premium Express DHL',
          costPaise: 199000,
          freeLimitPaise: 999900,
        },
      ],
    [setting?.value],
  );

  const [showForm, setShowForm] = useState(false);
  const [zone, setZone] = useState('');
  const [methodName, setMethodName] = useState('');
  const [cost, setCost] = useState('0');
  const [freeLimit, setFreeLimit] = useState('0');

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!zone || !methodName) return;
    const newMethod: ShippingMethod = {
      id: crypto.randomUUID(),
      zone,
      methodName,
      costPaise: Math.round(parseFloat(cost) * 100) || 0,
      freeLimitPaise: Math.round(parseFloat(freeLimit) * 100) || 0,
    };
    mutation.mutate([...methods, newMethod]);
    setZone('');
    setMethodName('');
    setCost('0');
    setFreeLimit('0');
    setShowForm(false);
  };

  const handleDelete = (id: string) => {
    mutation.mutate(methods.filter((m) => m.id !== id));
  };

  if (isLoading) {
    return (
      <div className="grid min-h-[300px] place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
      <div className="border-border/60 bg-surface-subtle/30 flex items-center justify-between border-b px-5 py-4">
        <div>
          <h2 className="text-ink text-[14px] font-bold">Shipping Zones & Rates</h2>
          <p className="text-ink-muted mt-0.5 text-[12px] font-medium">
            Configure flat rates, international zones, and free shipping thresholds.
          </p>
        </div>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="bg-ink hover:bg-ink/90 flex h-9 items-center gap-2 rounded-xl px-4 text-[13px] font-bold text-white transition-all"
        >
          <Plus className="h-4 w-4" />
          Add Zone
        </button>
      </div>

      {/* Add form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <form
              onSubmit={handleAdd}
              className="border-border/60 space-y-4 border-b bg-blue-50/30 p-5"
            >
              <p className="text-ink text-[13px] font-bold uppercase tracking-wider">
                New Shipping Zone
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  label="Zone / Country"
                  placeholder="e.g. Domestic (India)"
                  value={zone}
                  onChange={(e) => setZone(e.target.value)}
                  required
                />
                <Input
                  label="Method Name"
                  placeholder="e.g. Standard Surface"
                  value={methodName}
                  onChange={(e) => setMethodName(e.target.value)}
                  required
                />
                <Input
                  label="Cost (₹)"
                  placeholder="49"
                  value={cost}
                  onChange={(e) => setCost(e.target.value)}
                />
                <Input
                  label="Free Shipping Above (₹)"
                  placeholder="999"
                  value={freeLimit}
                  onChange={(e) => setFreeLimit(e.target.value)}
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="border-border/60 text-ink-muted hover:text-ink h-9 rounded-xl border px-4 text-[13px] font-bold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={mutation.isPending}
                  className="bg-ink hover:bg-ink/90 h-9 rounded-xl px-5 text-[13px] font-bold text-white disabled:opacity-50"
                >
                  Add Method
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
              <th className="py-3 pl-5 pr-4">Zone</th>
              <th className="py-3 pr-4">Method</th>
              <th className="py-3 pr-4 text-right">Cost</th>
              <th className="py-3 pr-4 text-right">Free Above</th>
              <th className="py-3 pr-5 text-right">Delete</th>
            </tr>
          </thead>
          <tbody className="divide-border/40 divide-y text-[13px]">
            {methods.map((m) => (
              <tr key={m.id} className="hover:bg-surface-subtle/30 transition-colors">
                <td className="text-ink py-4 pl-5 pr-4 font-bold">{m.zone}</td>
                <td className="text-ink-muted py-4 pr-4 font-medium">{m.methodName}</td>
                <td className="text-ink py-4 pr-4 text-right font-mono font-medium">
                  ₹{(m.costPaise / 100).toFixed(0)}
                </td>
                <td className="py-4 pr-4 text-right font-mono font-bold text-emerald-600">
                  {m.freeLimitPaise > 0 ? `₹${(m.freeLimitPaise / 100).toFixed(0)}` : 'No limit'}
                </td>
                <td className="py-4 pr-5 text-right">
                  <button
                    onClick={() => handleDelete(m.id)}
                    className="text-ink-muted inline-grid h-8 w-8 place-items-center rounded-lg transition-colors hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {methods.length === 0 && (
          <div className="p-8">
            <EmptyState
              icon={<Globe className="text-ink-muted h-8 w-8" />}
              title="No shipping zones"
              description="Add a shipping zone above to configure delivery rates."
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. Live Tracking Lookup Tab
// ─────────────────────────────────────────────────────────────────────────────
function TrackingLookupTab() {
  const [awbInput, setAwbInput] = useState('');
  const [queried, setQueried] = useState('');
  const [error, setError] = useState<string | null>(null);

  const {
    data: shipment,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['shipment-lookup', queried],
    queryFn: async () => {
      if (!queried) return null;
      const result = await api.orders.getShipmentByAwb(queried);
      return result;
    },
    enabled: !!queried,
    retry: false,
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!awbInput.trim()) return;
    setQueried(awbInput.trim());
  };

  // Determine progress index for timeline
  // Indices must line up with TRACKING_TIMELINE — a status missing here renders
  // as -1 and silently blanks the whole stepper.
  const progressMap: Record<string, number> = {
    PENDING: 0,
    AWB_ASSIGNED: 1,
    PICKUP_SCHEDULED: 2,
    PICKED_UP: 3,
    IN_TRANSIT: 4,
    OUT_FOR_DELIVERY: 5,
    DELIVERED: 6,
  };
  const progressIndex = shipment ? (progressMap[shipment.status] ?? -1) : -1;

  const isRto = shipment && ['RTO_INITIATED', 'RTO_DELIVERED'].includes(shipment.status);
  const isFailed = shipment && ['CANCELLED', 'FAILED'].includes(shipment.status);

  return (
    <div className="space-y-6">
      {/* Search form */}
      <div className="border-border/60 bg-surface rounded-2xl border p-5 shadow-sm">
        <form onSubmit={handleSearch} className="flex gap-3">
          <div className="relative flex-1">
            <Search className="text-ink-muted absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
            <input
              value={awbInput}
              onChange={(e) => setAwbInput(e.target.value)}
              placeholder="Enter AWB number..."
              className="border-border/60 bg-surface-subtle text-ink placeholder:text-ink-muted h-11 w-full rounded-xl border pl-9 pr-4 text-[14px] font-medium outline-none focus:ring-2 focus:ring-blue-500/30"
            />
          </div>
          <button
            type="submit"
            disabled={isLoading || isFetching}
            className="bg-ink hover:bg-ink/90 flex h-11 items-center gap-2 rounded-xl px-6 text-[13px] font-bold text-white disabled:opacity-50"
          >
            {isLoading || isFetching ? (
              <Spinner size="sm" className="text-white" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            Track
          </button>
        </form>
      </div>

      {/* Result */}
      {queried && !isLoading && !shipment && (
        <div className="border-border/60 bg-surface rounded-2xl border p-8 shadow-sm">
          <EmptyState
            icon={<AlertCircle className="text-ink-muted h-8 w-8" />}
            title="Shipment not found"
            description={`No shipment found for AWB "${queried}". Please verify the number.`}
          />
        </div>
      )}

      {shipment && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          {/* Summary card */}
          <div className="border-border/60 bg-surface rounded-2xl border p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-ink-muted text-[11px] font-bold uppercase tracking-wider">AWB</p>
                <p className="text-ink font-mono text-[18px] font-extrabold">
                  {shipment.awbNumber ?? queried}
                </p>
              </div>
              {shipment.courierName && (
                <div>
                  <p className="text-ink-muted text-[11px] font-bold uppercase tracking-wider">
                    Courier
                  </p>
                  <p className="text-ink text-[15px] font-bold">{shipment.courierName}</p>
                </div>
              )}
              <div>
                <p className="text-ink-muted text-[11px] font-bold uppercase tracking-wider">
                  Status
                </p>
                <Badge tone={STATUS_META[shipment.status as ShipmentStatus]?.tone ?? 'neutral'}>
                  {STATUS_META[shipment.status as ShipmentStatus]?.label ?? shipment.status}
                </Badge>
              </div>
              {shipment.weightGrams > 0 && (
                <div>
                  <p className="text-ink-muted text-[11px] font-bold uppercase tracking-wider">
                    Weight
                  </p>
                  <p className="text-ink text-[14px] font-bold">{shipment.weightGrams}g</p>
                </div>
              )}
            </div>
          </div>

          {/* Progress timeline */}
          {!isRto && !isFailed && (
            <div className="border-border/60 bg-surface rounded-2xl border p-6 shadow-sm">
              <p className="text-ink-muted mb-5 text-[11px] font-bold uppercase tracking-wider">
                Shipment Progress
              </p>
              <div className="relative flex items-center justify-between">
                {/* Line */}
                <div className="bg-surface-subtle absolute left-0 right-0 top-5 h-1 -translate-y-1/2 rounded-full" />
                <motion.div
                  className="absolute left-0 top-5 h-1 -translate-y-1/2 rounded-full bg-emerald-500"
                  initial={{ width: '0%' }}
                  animate={{
                    width:
                      progressIndex < 0
                        ? '0%'
                        : `${(progressIndex / (TRACKING_TIMELINE.length - 1)) * 100}%`,
                  }}
                  transition={{ duration: 0.7, ease: 'easeOut' }}
                />

                {TRACKING_TIMELINE.map((step, i) => {
                  const done = i <= progressIndex;
                  const current = i === progressIndex;
                  const IconComponent = step.icon;
                  return (
                    <div
                      key={step.status}
                      className="relative z-10 flex flex-col items-center gap-2"
                    >
                      <motion.div
                        initial={{ scale: 0.8 }}
                        animate={{ scale: current ? 1.1 : 1 }}
                        className={`flex h-10 w-10 items-center justify-center rounded-full border-2 transition-colors ${
                          done
                            ? 'border-emerald-500 bg-emerald-500 text-white shadow-md'
                            : 'border-border bg-surface text-ink-muted'
                        }`}
                      >
                        <IconComponent className="h-4 w-4" />
                      </motion.div>
                      <span
                        className={`text-center text-[10px] font-bold leading-tight ${
                          done ? 'text-emerald-600' : 'text-ink-muted'
                        }`}
                      >
                        {step.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {isRto && (
            <div className="rounded-2xl border border-orange-200 bg-orange-50 p-5">
              <div className="flex items-center gap-3">
                <RotateCcw className="h-5 w-5 text-orange-600" />
                <div>
                  <p className="text-[14px] font-bold text-orange-700">Return to Origin</p>
                  <p className="text-[12px] text-orange-600">
                    This shipment has been returned to the sender.
                  </p>
                </div>
              </div>
            </div>
          )}

          {isFailed && (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-5">
              <div className="flex items-center gap-3">
                <X className="h-5 w-5 text-red-600" />
                <div>
                  <p className="text-[14px] font-bold text-red-700">
                    Shipment {shipment.status === 'CANCELLED' ? 'Cancelled' : 'Failed'}
                  </p>
                  <p className="text-[12px] text-red-600">No further tracking is available.</p>
                </div>
              </div>
            </div>
          )}

          {/* Tracking events list */}
          {shipment.trackingEvents && shipment.trackingEvents.length > 0 && (
            <div className="border-border/60 bg-surface rounded-2xl border p-5 shadow-sm">
              <p className="text-ink-muted mb-4 text-[11px] font-bold uppercase tracking-wider">
                Tracking History
              </p>
              <div className="space-y-3">
                {shipment.trackingEvents.map((ev: any, i: number) => (
                  <div key={ev.id ?? i} className="flex items-start gap-3">
                    <div className="bg-ink/10 mt-2 h-2 w-2 shrink-0 rounded-full" />
                    <div>
                      <p className="text-ink text-[13px] font-bold">
                        {STATUS_META[ev.status as ShipmentStatus]?.label ?? ev.status}
                        {ev.location && (
                          <span className="text-ink-muted font-medium"> · {ev.location}</span>
                        )}
                      </p>
                      {ev.description && (
                        <p className="text-ink-muted text-[12px]">{ev.description}</p>
                      )}
                      <p className="text-ink-muted text-[11px]">
                        {new Date(ev.occurredAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Reusable Modal
// ─────────────────────────────────────────────────────────────────────────────
function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        transition={{ duration: 0.15 }}
        className="border-border/60 bg-surface w-full max-w-lg overflow-hidden rounded-2xl border shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-border/60 flex items-center justify-between border-b px-5 py-4">
          <h3 className="text-ink text-[15px] font-bold">{title}</h3>
          <button
            onClick={onClose}
            className="text-ink-muted hover:text-ink hover:bg-surface-subtle grid h-8 w-8 place-items-center rounded-lg transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </motion.div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Root page
// ─────────────────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'shipments', label: 'Shipments & AWBs', icon: Package },
  { id: 'integrations', label: 'Courier Integrations', icon: Settings },
  { id: 'zones', label: 'Shipping Zones', icon: Globe },
  { id: 'tracking', label: 'Live Tracking', icon: Zap },
  { id: 'money', label: 'Courier Money', icon: Wallet },
] as const;

type TabId = (typeof TABS)[number]['id'];

export function LogisticsPage() {
  const [activeTab, setActiveTab] = useState<TabId>('shipments');

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="border-border/60 bg-surface flex flex-col justify-between gap-4 rounded-2xl border p-5 shadow-sm sm:flex-row sm:items-center">
        <div>
          <h3 className="text-ink text-[16px] font-bold tracking-tight">Logistics & Shipping</h3>
          <p className="text-ink-subtle mt-0.5 text-[13px] font-medium">
            Manage courier integrations, AWB assignments, tracking events and shipping zones.
          </p>
        </div>
        <div className="flex items-center gap-2 text-[12px]">
          <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 font-bold text-emerald-700">
            Shiprocket-native
          </span>
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-border/60 bg-surface flex flex-wrap gap-2 rounded-2xl border p-2 shadow-sm">
        {TABS.map((t) => (
          <Tab
            key={t.id}
            active={activeTab === t.id}
            onClick={() => setActiveTab(t.id)}
            icon={t.icon}
            label={t.label}
          />
        ))}
      </div>

      {/* Tab panels */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.15 }}
        >
          {activeTab === 'shipments' && <ShipmentsTab />}
          {activeTab === 'integrations' && <CourierIntegrationsTab />}
          {activeTab === 'zones' && <ShippingZonesTab />}
          {activeTab === 'tracking' && <TrackingLookupTab />}
          {activeTab === 'money' && <CourierMoneyTab />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
